import { AppDispatch } from 'store';
import { PaginationProps } from 'types/paginationAndFilter';
import { SystemPrivilegeListItem } from 'types/systemPrivileges';
import { portalAPI } from './portalAPI';
import { accumulatedQueryWithFilters } from './util/endpointBuilder';

// Structural subset of the onQueryStarted getState() result — only the fields this helper reads.
type QueriesState = {
    portalAPI: { queries: Record<string, { endpointName?: string; originalArgs?: unknown } | undefined> };
};

// Optimistically patch every accumulated privilege list cache in place: find the row by id in each
// getSystemPrivilegesWithFilters query and mutate it via `apply`. Returns the patch handles so the
// caller can undo them if the request fails. No refetch/invalidation — that would disturb unsaved
// checkbox edits on the same table.
const patchAccumulatedPrivilegeCaches = (
    getState: () => QueriesState,
    dispatch: AppDispatch,
    id: number,
    apply: (item: SystemPrivilegeListItem) => void
) => {
    const queries = getState().portalAPI.queries;
    return Object.entries(queries)
        .filter((entry) => entry[1]?.endpointName === 'getSystemPrivilegesWithFilters')
        .map((entry) =>
            dispatch(
                APISystemPrivilegesSlice.util.updateQueryData(
                    'getSystemPrivilegesWithFilters',
                    entry[1]?.originalArgs as PaginationProps,
                    (draft) => {
                        const item = draft.content.find((row) => row.id === id);
                        if (item !== undefined) {
                            apply(item);
                        }
                    }
                )
            )
        );
};

export const APISystemPrivilegesSlice = portalAPI
    .enhanceEndpoints({ addTagTypes: ['SystemPrivileges'] })
    .injectEndpoints({
        endpoints: (builder) => ({
            getSystemPrivilegesWithFilters: accumulatedQueryWithFilters<SystemPrivilegeListItem>(
                builder,
                (roleId) => `/v1/system-privileges/${roleId}`,
                {
                    providesTags: ['SystemPrivileges'],
                    sort: ['code,asc'],
                }
            ),
            updateSystemPrivilegeDescription: builder.mutation<
                SystemPrivilegeListItem,
                { id: number; description: string }
            >({
                query: ({ id, description }) => ({
                    url: `/v1/system-privileges/${id}/description`,
                    method: 'PATCH',
                    body: { description },
                }),
                async onQueryStarted({ id, description }, { dispatch, queryFulfilled, getState }) {
                    const patches = patchAccumulatedPrivilegeCaches(getState, dispatch, id, (item) => {
                        item.description = description;
                    });
                    try {
                        await queryFulfilled;
                    } catch {
                        patches.forEach((patch) => patch.undo());
                    }
                },
            }),
            updateSystemPrivilegeMinimumRequired: builder.mutation<
                SystemPrivilegeListItem,
                { id: number; isMinimumRequiredPrivilege: boolean }
            >({
                query: ({ id, isMinimumRequiredPrivilege }) => ({
                    url: `/v1/system-privileges/${id}/minimum-required`,
                    method: 'PATCH',
                    body: isMinimumRequiredPrivilege,
                }),
                async onQueryStarted({ id, isMinimumRequiredPrivilege }, { dispatch, queryFulfilled, getState }) {
                    const patches = patchAccumulatedPrivilegeCaches(getState, dispatch, id, (item) => {
                        item.isMinimumRequiredPrivilege = isMinimumRequiredPrivilege;
                    });
                    try {
                        await queryFulfilled;
                    } catch {
                        patches.forEach((patch) => patch.undo());
                    }
                },
            }),
            updateSystemPrivilegeVisibleFor: builder.mutation<
                SystemPrivilegeListItem,
                { id: number; visibleFor: string | null }
            >({
                query: ({ id, visibleFor }) => ({
                    url: `/v1/system-privileges/${id}/visible-for`,
                    method: 'PATCH',
                    body: { visibleFor },
                }),
                async onQueryStarted({ id, visibleFor }, { dispatch, queryFulfilled, getState }) {
                    const patches = patchAccumulatedPrivilegeCaches(getState, dispatch, id, (item) => {
                        item.visibleFor = visibleFor;
                    });
                    try {
                        await queryFulfilled;
                    } catch {
                        patches.forEach((patch) => patch.undo());
                    }
                },
            }),
        }),
        overrideExisting: false,
    });

export const {
    useLazyGetSystemPrivilegesWithFiltersQuery,
    useUpdateSystemPrivilegeDescriptionMutation,
    useUpdateSystemPrivilegeMinimumRequiredMutation,
    useUpdateSystemPrivilegeVisibleForMutation,
} = APISystemPrivilegesSlice;
